import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import http, { formatApiErrorDetail, suppliers as suppliersApi } from "@/api";
import { PILLARS } from "@/constants";
import {
  CheckCircle2, XCircle, FileText, ArrowLeft, Edit3, Eye, ShieldCheck,
  SlidersHorizontal, CheckSquare, ListChecks, Type, Info, RotateCcw,
} from "lucide-react";

const TYPE_META = {
  scale:           { label: "Escala 0–10",     icon: SlidersHorizontal, color: "var(--lala-blue)" },
  yes_no:          { label: "Sí / No / NA",    icon: CheckSquare,       color: "#E4002B" },
  multiple_choice: { label: "Opción múltiple", icon: ListChecks,        color: "#D4A437" },
  open:            { label: "Texto abierto",   icon: Type,              color: "#009639" },
};

const STATUS_META = {
  draft:     { label: "Borrador",    color: "bg-stone-100 text-stone-800 border-stone-300",     icon: Edit3 },
  in_review: { label: "En revisión", color: "bg-amber-100 text-amber-900 border-amber-300",     icon: Eye },
  validated: { label: "Validado",    color: "bg-green-100 text-green-900 border-green-300",     icon: ShieldCheck },
  rejected:  { label: "Rechazado",   color: "bg-red-100 text-red-900 border-red-300",           icon: XCircle },
};

export default function QuestionnaireReview() {
  const [supps, setSupps] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [responses, setResponses] = useState([]);
  const [active, setActive] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [subKpis, setSubKpis] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    suppliersApi.list().then(setSupps);
    http.get("/questions", { params: { active_only: false } }).then((r) => setQuestions(r.data));
    http.get("/sub-kpis").then((r) => setSubKpis(r.data || []));
  }, []);

  useEffect(() => {
    if (!supplierId) { setResponses([]); setActive(null); return; }
    http.get(`/suppliers/${supplierId}/questionnaire-responses`).then((r) => {
      setResponses(r.data);
      setActive(r.data[0] || null);
    });
  }, [supplierId]);

  const qById = useMemo(() => Object.fromEntries(questions.map((q) => [q.id, q])), [questions]);
  const subKpiById = useMemo(() => Object.fromEntries((subKpis || []).map((s) => [s.id, s])), [subKpis]);

  const validate = async () => {
    if (!active) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const res = await http.post(`/questionnaire-responses/${active.id}/validate`);
      setActive(res.data);
      setResponses((rs) => rs.map((r) => r.id === res.data.id ? res.data : r));
      setMsg("Cuestionario validado. Las respuestas alimentan el scorecard del proveedor.");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!active) return;
    const note = window.prompt("Motivo del rechazo (se envía al proveedor):") || "";
    setBusy(true); setErr(""); setMsg("");
    try {
      const form = new FormData();
      form.append("note", note);
      const res = await http.post(`/questionnaire-responses/${active.id}/reject`, form);
      setActive(res.data);
      setResponses((rs) => rs.map((r) => r.id === res.data.id ? res.data : r));
      setMsg("Cuestionario rechazado. El proveedor podrá corregirlo y reenviar.");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const overrideScore = async (questionId, value) => {
    setBusy(true); setErr("");
    try {
      const form = new FormData();
      if (value !== null && value !== undefined && value !== "") form.append("evaluator_score", String(value));
      const res = await http.put(`/questionnaire-responses/${active.id}/answers/${questionId}/score`, form);
      setActive(res.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const downloadEvidence = (docId) => {
    if (!docId) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/download`, "_blank");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/dashboard" data-testid="back-dashboard"
        className="text-sm text-[var(--lala-blue)] inline-flex items-center gap-1 hover:underline">
        <ArrowLeft size={14} /> Volver al dashboard
      </Link>
      <div className="mt-2 mb-6">
        <div className="font-hand text-xl text-[var(--lala-blue)]">revisión Lala</div>
        <h1 className="font-display text-4xl">Cuestionarios de proveedores</h1>
        <p className="text-[var(--ink-soft)] mt-1 max-w-3xl">
          Revisa cada respuesta, ajusta el puntaje si la evidencia justifica algo distinto, y valida o rechaza el cuestionario.
        </p>
      </div>

      {/* How to review helper */}
      <details className="sketch-border bg-[var(--cream-soft)] p-5 mb-6" data-testid="how-to-review">
        <summary className="cursor-pointer font-display text-lg text-[var(--lala-blue)] inline-flex items-center gap-2 select-none">
          <Info size={18} /> ¿Cómo se revisa un cuestionario?
        </summary>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm mt-4">
          <Helper title="1 · Selecciona proveedor y período"
            body="A la izquierda elige al proveedor y luego el período del cuestionario que quieres revisar." />
          <Helper title="2 · Lee cada respuesta y su evidencia"
            body="El score sugerido viene del proveedor o de las opciones que él eligió. Si hay evidencia adjunta, ábrela para verificar." />
          <Helper title="3 · Override si aplica"
            body="Si la evidencia justifica otro puntaje, escribe tu score 0–10 en el campo 'Tu evaluación' y guarda. Queda registrado el ajuste." />
          <Helper title="4 · Valida o rechaza"
            body="Validar = alimenta el scorecard del proveedor. Rechazar = lo regresa al proveedor con tu nota para que corrija." />
        </div>
      </details>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Proveedor</label>
            <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
              className="input-field mt-1" data-testid="rev-supplier">
              <option value="">Selecciona…</option>
              {supps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div className="space-y-2" data-testid="rev-responses-list">
            {responses.map((r) => {
              const sm = STATUS_META[r.status] || STATUS_META.draft;
              const sel = active?.id === r.id;
              return (
                <button key={r.id} onClick={() => setActive(r)}
                  data-testid={`rev-resp-${r.id}`}
                  className={`w-full text-left p-3 sketch-border-soft transition-colors ${
                    sel ? "bg-[var(--lala-blue)] text-[var(--cream)]" : "bg-[var(--cream)] hover:bg-[var(--cream-soft)]"
                  }`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{r.period}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${sm.color}`}>
                      {sm.label}
                    </span>
                  </div>
                  <div className="text-xs opacity-80 mt-1">{r.answers?.length || 0} respuestas</div>
                </button>
              );
            })}
            {responses.length === 0 && supplierId && (
              <div className="text-sm text-[var(--ink-soft)] italic">Sin cuestionarios para este proveedor.</div>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          {!active && <div className="text-[var(--ink-soft)] italic">Selecciona un proveedor y un período para revisar.</div>}
          {active && (
            <div className="space-y-5" data-testid="rev-detail">
              <div className="flex flex-wrap items-center justify-between gap-2 sketch-border-soft bg-[var(--cream)] p-4">
                <div>
                  <div className="font-display text-2xl">{active.supplier_name}</div>
                  <div className="text-xs text-[var(--ink-soft)] mt-1 inline-flex items-center gap-2">
                    Período {active.period} · Estado:
                    <StatusBadge status={active.status} />
                  </div>
                  {active.status === "rejected" && active.rejection_note && (
                    <div className="text-xs text-red-800 italic mt-1">Nota anterior: "{active.rejection_note}"</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={validate} disabled={busy || active.status === "validated"}
                    data-testid="rev-validate-btn"
                    className="sketch-pill bg-[var(--lala-blue)] text-[var(--cream)] px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-40">
                    <CheckCircle2 size={14} /> Validar
                  </button>
                  <button onClick={reject} disabled={busy}
                    data-testid="rev-reject-btn"
                    className="sketch-pill bg-[var(--cream)] text-red-700 border border-red-300 px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-40">
                    <XCircle size={14} /> Rechazar
                  </button>
                  <button onClick={() => nav(`/evaluar?supplier=${active.supplier_id}&period=${active.period}`)}
                    data-testid="rev-to-scorecard-btn"
                    className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1">
                    <FileText size={14} /> Al scorecard
                  </button>
                </div>
              </div>

              {msg && <div className="text-green-700 bg-green-50 border border-green-200 rounded-lg p-2 text-sm">{msg}</div>}
              {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 text-sm">{err}</div>}

              {PILLARS.map((p) => {
                const items = (active.answers || []).filter((a) => qById[a.question_id]?.pillar_key === p.key);
                if (items.length === 0) return null;
                return (
                  <div key={p.key} className="sketch-border-soft bg-[var(--cream-soft)] p-4" data-testid={`rev-pillar-${p.key}`}>
                    <h3 className="font-display text-xl text-[var(--lala-blue)] mb-3">{p.label}</h3>
                    <div className="space-y-3">
                      {items.map((a) => {
                        const q = qById[a.question_id];
                        if (!q) return null;
                        const subKpi = q.sub_kpi_id ? subKpiById[q.sub_kpi_id] : null;
                        return (
                          <AnswerCard key={a.question_id} q={q} a={a} subKpi={subKpi}
                            readonly={active.status === "validated"}
                            onOverride={(v) => overrideScore(a.question_id, v)}
                            onOpenEvidence={() => downloadEvidence(a.evidence_document_id)} />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Helper({ title, body }) {
  return (
    <div className="flex items-start gap-2">
      <CheckCircle2 size={14} className="text-[var(--lala-blue)] mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold text-[var(--ink)]">{title}</div>
        <div className="text-[var(--ink-soft)]">{body}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const sm = STATUS_META[status] || STATUS_META.draft;
  const Icon = sm.icon;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${sm.color}`}>
      <Icon size={10} /> {sm.label}
    </span>
  );
}

function AnswerCard({ q, a, subKpi, readonly, onOverride, onOpenEvidence }) {
  const typeMeta = TYPE_META[q.type] || TYPE_META.scale;
  const TypeIcon = typeMeta.icon;
  const [override, setOverride] = useState(a.evaluator_score ?? "");

  const renderValue = () => {
    if (q.type === "scale") return (
      <div className="inline-flex items-baseline gap-2">
        <span className="font-display text-3xl text-[var(--lala-blue)]">{Number(a.value_scale ?? 0).toFixed(1)}</span>
        <span className="text-xs text-[var(--ink-soft)]">/ 10</span>
      </div>
    );
    if (q.type === "yes_no") {
      const map = { yes: "Sí", no: "No", na: "N/A" };
      const v = (a.value_yes_no || "").toLowerCase();
      const color = v === "yes" ? "bg-green-50 text-green-800 border-green-300"
                  : v === "no"  ? "bg-red-50 text-red-800 border-red-300"
                  : "bg-stone-100 text-stone-700 border-stone-300";
      return <span className={`px-3 py-1 rounded-full border text-sm font-semibold ${color}`}>{map[v] || "—"}</span>;
    }
    if (q.type === "multiple_choice") {
      const opt = (q.options || [])[a.value_choice_index];
      if (!opt) return <span className="text-[var(--ink-soft)] italic">— sin respuesta —</span>;
      return (
        <div className="inline-flex items-center gap-2">
          <span className="px-2 py-1 rounded-md bg-[var(--cream)] border border-[var(--lala-blue)]/30 text-sm">{opt.label}</span>
          {opt.score != null && <span className="text-sm text-[var(--ink-soft)]">→ score {opt.score}</span>}
        </div>
      );
    }
    if (q.type === "open") return (
      <div className="text-sm italic bg-[var(--cream)] border border-[var(--line)] rounded-lg p-2 max-w-full">"{a.value_text || "— sin respuesta —"}"</div>
    );
    return null;
  };

  const hasOverride = a.evaluator_score !== null && a.evaluator_score !== undefined;

  return (
    <div className="bg-[var(--cream)] sketch-border-soft p-4" data-testid={`rev-answer-${q.id}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ backgroundColor: typeMeta.color }}>
          <TypeIcon size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: typeMeta.color, color: "white" }}>{typeMeta.label}</span>
            {subKpi && (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-300">
                sub-KPI · {subKpi.label}
              </span>
            )}
            {q.feeds_scorecard ? (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[var(--lala-blue-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]/30 inline-flex items-center gap-1">
                <CheckCircle2 size={10} /> Alimenta scorecard
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 border border-stone-300">
                Contexto cualitativo
              </span>
            )}
          </div>
          <div className="font-semibold text-sm">{q.text}</div>
          {q.help_text && <div className="text-xs text-[var(--ink-soft)] italic mt-0.5">{q.help_text}</div>}

          <div className="mt-3 flex flex-wrap items-start gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Respuesta del proveedor</div>
              <div className="mt-1">{renderValue()}</div>
            </div>
            {a.evidence_document_id && (
              <button onClick={onOpenEvidence}
                data-testid={`open-evidence-${q.id}`}
                className="self-end sketch-pill bg-[var(--cream-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]/40 px-3 py-1 text-xs font-semibold inline-flex items-center gap-1 hover:bg-[var(--lala-blue-soft)]">
                <FileText size={12} /> Abrir evidencia
              </button>
            )}
          </div>

          {q.feeds_scorecard && (
            <div className="mt-3 sketch-border-soft bg-[var(--cream-soft)] p-3" data-testid={`override-box-${q.id}`}>
              <div className="flex items-center gap-2 text-xs font-semibold text-[var(--lala-blue)] mb-2">
                <Edit3 size={12} /> Tu evaluación (override)
                {hasOverride && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-800 border border-green-300">
                    aplicado · {a.evaluator_score}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input value={override}
                  onChange={(e) => setOverride(e.target.value)}
                  type="number" min="0" max="10" step="0.1"
                  disabled={readonly}
                  className="input-field py-1 px-2 w-24 text-sm"
                  placeholder="0–10"
                  data-testid={`override-${q.id}`} />
                <button onClick={() => onOverride(override === "" ? null : Number(override))}
                  disabled={readonly}
                  data-testid={`override-save-${q.id}`}
                  className="sketch-pill bg-[var(--lala-blue)] text-[var(--cream)] px-3 py-1 text-xs font-semibold disabled:opacity-50">
                  Guardar override
                </button>
                {hasOverride && !readonly && (
                  <button onClick={() => { setOverride(""); onOverride(null); }}
                    data-testid={`override-clear-${q.id}`}
                    className="sketch-pill bg-[var(--cream)] text-[var(--ink-soft)] border border-[var(--line)] px-3 py-1 text-xs font-semibold inline-flex items-center gap-1">
                    <RotateCcw size={11} /> Limpiar
                  </button>
                )}
                <span className="text-xs text-[var(--ink-soft)] italic">
                  Deja vacío para usar el score automático del proveedor.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
